import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type DateRange } from '~/types/common';
import { type UncefactUnitType } from '~/types/unit';

import { useQueryDeliveryNoteAnalytics } from '~/data/deliveryNote';

import { Spinner } from '@vestigas/ui/molecules';
import { withErrorBoundary } from '~/ui/atoms';

import { type AggregationInterval } from '../types';
import { generatePieChartData, generateTimeSeriesChartData } from '../utils';

import { DashboardDetailOverview } from './DashboardDetailOverview';
import { DoughnutChart } from './DoughnutChart';
import { TimeSeriesChart } from './TimeSeriesChart';

type P = {
  aggregationInterval: AggregationInterval;
  searchBody: any;
  selectedDateRange: DateRange;
  selectedUnit: UncefactUnitType | '';
};

export const DashboardCharts = withErrorBoundary(
  ({ aggregationInterval, searchBody, selectedDateRange, selectedUnit }: P) => {
    const { data, isFetching, isLoading } = useQueryDeliveryNoteAnalytics(
      {
        ...searchBody,
        timeAggregate: aggregationInterval,
        unitType: selectedUnit,
      },
      {
        enabled: Boolean(selectedUnit) && Boolean(aggregationInterval),
        placeholderData: keepPreviousData,
      },
    );

    const distributionData = useMemo(
      () => generatePieChartData(data ?? []),
      [data],
    );

    const timeSeriesData = useMemo(
      () =>
        generateTimeSeriesChartData(
          data ?? [],
          selectedDateRange,
          aggregationInterval,
        ),
      [aggregationInterval, data, selectedDateRange],
    );

    const noData = useMemo(
      () =>
        (!data || data.length === 0) &&
        !isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="h-auto w-auto bg-white px-4 py-3 text-sm">
              Keine Lieferungen gefunden für die gewählten Filtereinstellungen.
            </p>
          </div>
        ),
      [data, isLoading],
    );

    const timeSeriesChart = useMemo(
      () => (
        <TimeSeriesChart
          aggregationInterval={aggregationInterval}
          className="rounded-md border border-gray-200 px-4 pt-4 pb-2"
          data={timeSeriesData}
          isLoading={isFetching}
          selectedUnit={selectedUnit}
        />
      ),
      [aggregationInterval, isFetching, selectedUnit, timeSeriesData],
    );

    const detailOverview = useMemo(
      () => (
        <DashboardDetailOverview
          data={data ?? []}
          isLoading={isFetching}
          selectedUnit={selectedUnit}
        />
      ),
      [data, selectedUnit, isFetching],
    );

    if (!selectedUnit) {
      return (
        <div className="flex h-96 w-full flex-col items-center justify-center">
          <div>
            Bitte wähle eine Einheit aus, für die die Diagramme erstellt werden
            sollen.
          </div>
        </div>
      );
    }

    const hasDistributionData =
      (distributionData.datasets[0]?.data?.length ?? 0) > 0;

    return (
      <div className="flex flex-col gap-8 pt-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-medium">Verteilung</h2>
          <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="w-full">{detailOverview}</div>
            <div className="flex h-[40vh] min-h-[389px] w-full items-center justify-center rounded-md border border-gray-200 p-4">
              <div className="relative h-full w-full">
                {isFetching ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center">
                    <Spinner />
                  </div>
                ) : null}
                {hasDistributionData ? (
                  <DoughnutChart
                    chartData={distributionData}
                    className="absolute inset-0"
                    selectedUnit={selectedUnit}
                  />
                ) : null}
                {noData}
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex flex-col gap-1">
          <h2 className="text-xl font-medium">Zeitlicher Verlauf</h2>
          {timeSeriesChart}
          {noData}
        </div>
      </div>
    );
  },
  'Diagramme konnten nicht geladen werden.',
);

DashboardCharts.displayName = 'DashboardCharts';
